'use strict';

var d3 = require('d3');

/**
 * Chart Viewport
 * @param AbstractChart
 * @param chartBrush
 * @returns {ChartViewport}
 * @constructor
 */
var ChartViewport = function ChartViewport(AbstractChart, chartBrush, chartSparseBrush) {
  /**
   * @param container - a container where the chart will be rendered
   * @param chartParams - a params function
   * @param chartScales - a scales function
   * @returns {Function}
   * @constructs ChartViewport
   */
  var ChartViewport = function ChartViewport(container, chartParams, chartScales, groups) {
    AbstractChart.call(this, container);

    // retrieve the drawing functions of viewport elements
    var elementFunctions = [].slice.call(arguments, 3);
    var dispatch = d3.dispatch('rangechange');
    d3.rebind(this, dispatch, 'on');
    var brush = chartBrush();
    if (chartParams().isSparse) {
      brush = chartSparseBrush();
      this.destroyableElements = this.destroyableElements.concat([brush]);
    }

    /**
     * Returns selection range
     * @param {number} selectionSize
     * @param {number} domainLength
     * @returns {number[]}
     */
    function getSelectionRange(selectionSize, domainLength) {
      if (selectionSize > 0) {
        return [0, Math.min(selectionSize, domainLength)];
      } else {
        return [Math.max(0, domainLength + selectionSize), domainLength];
      }
    }

    /**
     * Changes selection range
     * @param {number} selectionSize
     * @param {number} domainLength
     */
    function updateBrushSelection(selectionSize, domainLength) {
      var initializingChart = selectionSize !== undefined;
      var resizingChart = selectionSize === undefined;
      if (initializingChart) {
        brush.setSelection(getSelectionRange(selectionSize, domainLength));
      }
      if (resizingChart) {
        brush.setSelection(brush.getSelection());
      }
    }

    /**
     *
     * @param {Array} data - viewport data; if resizing, data will be empty
     * @param {number} selectionSize
     * @param {number} maxBrushSize
     */
    this.render = function (data, selectionSize, maxBrushSize) {
      this.visibleXRange = undefined;

      // null - always show the full range for the viewport
      this.initChart(data, undefined, chartParams);
      if (maxBrushSize && typeof selectionSize === 'number') {
        selectionSize = Math.min(selectionSize, maxBrushSize);
        brush.withSizeRange({
          max: maxBrushSize
        });
      }
      this.scales = chartScales(this.params, this.data, this.visibleXRange);
      this.callForDataGroup(elementFunctions);
      this.callForPostDataGroup([brush.withParams(this.params).withDomainRange(this.visibleXRange)], this.scales);
      updateBrushSelection(selectionSize, this.scales.xScale.domain().length);
    };

    /**
     * Sets brush max size
     * @param {number} max
     */
    this.setBrushMaxSize = function (max) {
      var selection = brush.getSelection();
      brush.withSizeRange({
        max: max
      });
      if (selection) {
        selection[1] = Math.min(selection[1], selection[0] + max);
        brush.setSelection(selection, true);
      }
    };

    /**
     * Handler called when selection on main chart is changed
     * @param ranges
     */
    this.onMainChartBrushChange = function (ranges) {
      this.brushChangeAction(ranges, groups);
    };

    /**
     * Set Initial Brush selection
     * @param range
     * @param silent
     */
    this.setInitialSelection = function (range, silent) {
      brush.setSelection(range, silent);
    };
    this.getSelection = function () {
      return brush.getSelection();
    };

    /**
     * Resets brush selection
     * @param {number} [selection] - selection size. If not provided all viewport will be selected
     */
    this.resetBrush = function (selection) {
      var domainLength = this.scales.xScale.domain().length;
      updateBrushSelection(selection || domainLength, domainLength);
    };
    brush.on('brushchange', function (range) {
      dispatch.rangechange(range);
    });
    container.on('$destroy', function () {
      dispatch.on('rangechange', null);
      brush.on('brushchange', null);
    });
  };
  ChartViewport.prototype = Object.create(AbstractChart.prototype);
  ChartViewport.constructor = ChartViewport;
  return ChartViewport;
};
ChartViewport.$inject = ['AbstractChart', 'ChartElementsBrush', 'ChartElementsBrushSparse'];
module.exports = ChartViewport;